import type { PageCard } from "../../core/types.js";
import type { AgentTurnStatus } from "./turn-status-schema.js";
import type { PromptAuthor } from "../../turn-author-identity.js";
import type { PeerRelayMark } from "./peer-message.js";
import type { Attachment } from "../../types.js";

export interface Agent {
  agentId: string;
  provider: string;
  lifecycle: string;
  channelId?: string | null;
  cyboId?: string | null;
  cyboName?: string | null;
  cyboAvatar?: string | null;
  // CYBORG-1312 (#3734): the Claude session's peer name (what native SendMessage addresses);
  // only on the caller's own rows. Peer cards read it to put a face on a peer.
  peerName?: string | null;
  // The session's SHARED name: one per session, the same for everyone who may see it, set
  // when its owner names it (relay-stamped). Absent when it has none, or from an older relay.
  sharedName?: string | null;
  initiatedBy?: string | null;
  // The initiator's canonical email — the stable cross-namespace identity the
  // daemon/relay stamp on every agent row (dispatcher `toAgentListRow` /
  // `buildOfflineAgentRow`). `initiatedBy` is the OWNING daemon's LOCAL id for the
  // launcher, so for a session started on ANOTHER daemon it does NOT match any
  // workspace member id; this email is the fallback that resolves the owner's real
  // name (session-scope `resolveOwnerName`). Optional + additive.
  initiatedByEmail?: string | null;
  // CYBO-1846, server-computed (the daemon's own channel and session facts): the session lives in a
  // REGULAR channel (the read-only wall is anonymous there), and it is someone's private DM session
  // (an admin may not clone it). Absent from an older daemon = false, today's behaviour.
  regularChannel?: boolean;
  dmSession?: boolean;
  model?: string | null;
  modeId?: string | null;
  availableModes?: AgentMode[];
  thinkingOptionId?: string | null;
  cwd?: string | null;
  daemonLocal?: boolean;
  daemonId?: string | null;
  // The persistent agent→human DEFAULT session (knowledge/83) — a record-only,
  // recipient-owned DM surface (agentId `dmdef-<cybo>-<user>`). Forwarded on the
  // cyborg:list_agents offline rows. When true (or the id has the `dmdef-` prefix) the
  // agent view renders the cybo↔user DM thread instead of the agent stream, and the
  // sidebar (Phase 4) shows just this one session per cybo. Optional/additive.
  isDefault?: boolean;
  // Daemon-derived "needs attention" signal (#591), forwarded on the agent-list
  // snapshot (cyborg:list_agents). Edge-triggered server-side: set on
  // running→idle ("finished") and →error, cleared when the agent is viewed.
  // Optional + additive (older daemons omit it). The agents list reads this to
  // badge a finished/errored background agent; the live-derived equivalent for
  // agents already in view comes from the stream events (see attentionState).
  attention?: AgentAttention;
  // The agent's ordered turn status (knowledge/126 step 2b); absent from an older daemon.
  turnRecord?: AgentTurnStatus;
  // Legacy alias of turnRecord (#3754); read `turnRecord ?? turnStatus` via wireTurnRecord.
  turnStatus?: AgentTurnStatus;
  // Daemon-owner audit badges (#993). `ephemeral` = a one-turn slash/@mention summon;
  // `internal` = the Paseo internal flag (set on a live ephemeral). The audit
  // SessionList badges rows where either is true as "Ephemeral".
  //
  // `ephemeral` is now ALSO carried on the scoped cyborg:list_agents rows that feed
  // the chat sidebar (dispatcher `toAgentListRow`): a live ephemeral binding is
  // owner-scoped, so it DOES show up in its owner's scoped list while it infers. The
  // sidebar's "mine" filter (session-scope `belongsInMineSidebar`) reads this flag to
  // exclude the summon from the FIRST render, so it never flickers in. `internal`
  // stays audit-only. The daemon-detail audit lens still shows ephemerals.
  ephemeral?: boolean;
  internal?: boolean;
  // The parent agent that spawned this one (Paseo's agent-to-agent orchestration:
  // the PARENT_AGENT_ID_LABEL, set when a create_agent MCP call carries a caller
  // agent id). Forwarded on the cyborg:list_agents snapshot from the live
  // ManagedAgent's labels (dispatcher liveAgentFields). Present only for LIVE
  // agents on a connected daemon — same limitation as `attention` — since bindings
  // don't persist labels. The agent-session view groups a parent's sub-agents by
  // this. Absent for a top-level agent and for Claude's native Task sub-agents
  // (an in-process sidechain with no separate managed agent).
  parentAgentId?: string | null;
}

// The agent-list row's attention projection. `reason` distinguishes a finished
// turn from an error so the badge can label them differently; "permission" is
// also possible (kept for completeness) though the pending-permission badge has
// its own, separate surface.
export interface AgentAttention {
  requiresAttention: boolean;
  reason?: AgentAttentionReason | null;
}

export type AgentAttentionReason = "finished" | "error" | "permission";

// Cybo runtime capability published by ≥this-build daemons (knowledge/25 item 3):
// configured = the runtime lists ≥1 model (credentials PRESENT — not validated;
// an invalid key still lists models and only fails at session time);
// backends = per-backend model counts when the runtime exposes them. Absent on
// older daemons (or while pi is still loading) → capability unknown.
export interface CyboRuntimeProfile {
  configured: boolean;
  modelCount: number;
  backends: { backend: string; modelCount: number }[];
}

export interface DaemonLastUpdate {
  requestId?: string;
  // Every request this outcome answers (the desktop app coalesces requests).
  requestIds?: string[];
  outcome: "updated" | "up_to_date" | "rolled_back" | "failed";
  from?: string;
  to?: string;
  at: number;
  error?: string;
}

// Mirrors the relay's DaemonAppLocationSchema (relay-protocol.ts).
export interface DaemonAppLocation {
  locationClass: "applications" | "user_applications" | "translocated" | "disk_image" | "other";
  isTranslocated: boolean;
  isReadOnlyVolume: boolean;
  quarantined: boolean;
  at: number;
}

// Mirrors the relay's DaemonAppUpdateProgressSchema (relay-protocol.ts): a new app's updater.
export interface DaemonAppUpdateProgress {
  phase: "idle" | "checking" | "downloading" | "downloaded" | "installing" | "failed";
  version?: string;
  percent?: number;
  installBy?: number;
  reason?: "network" | "read_only_volume" | "disk_full" | "other";
  since: number;
  at: number;
}

// Mirrors cyborg-messages.ts DesktopUpdateNudgeStatusSchema: the last legacy Update app nudge.
export interface DesktopUpdateNudgeStatus {
  requestedAt: number;
  windowMs: number;
  fromVersion: string;
  outcome?: "updated" | "no_change" | "timeout";
  settledAt?: number;
  toVersion?: string;
  reached?: boolean;
  match?: "hello_daemon_id" | "enrollment";
  unreached?: "target_app_not_open" | "cannot_identify_target_app" | "delivery_unconfirmed";
  notified?: number;
}

export interface DesktopRepairOffer {
  level: "secondary" | "promoted";
  reason: "behind_latest" | "update_not_installed" | "blocked";
}

// Relay-derived (list_daemons), so it lives on the row, not in meta.
export interface DesktopUpdateStatus {
  behindLatest?: string;
  repairOffer?: DesktopRepairOffer;
  nudge?: DesktopUpdateNudgeStatus;
}

// The relay's verdict on whether that machine's app can update itself (cyborg-messages.ts
// DesktopUpdateBlockedSchema). Relay-derived, so it lives on the row, not in meta.
export interface DesktopUpdateBlocked {
  reason: "translocated" | "read_only_volume" | "stuck";
  since?: number;
  latest?: string;
}

export interface DaemonMeta {
  cpu?: number;
  memMb?: number;
  agents?: number;
  queueDepth?: number;
  host?: string;
  platform?: string;
  arch?: string;
  // Running daemon CLI version (#663) — drives the "outdated → Update" UI.
  // Optional/back-compat: older daemons don't publish it.
  //
  // NOT a feature gate: it is read from packages/server/package.json
  // (daemon-relay-client.ts), which is frozen at 0.1.105 — only packages/cli is
  // versioned from the release tag. A `version >= X` check is permanently false.
  // Use `capabilities` below.
  version?: string;
  // The Cyborg DESKTOP app version on that machine (0.0.x), set only by the desktop
  // shell. On a desktop daemon this — not `version` — is what "update" moves.
  appVersion?: string;
  // The machine's IANA zone: schedules made there without one run in it. Absent = unknown
  // (an older daemon).
  timeZone?: string;
  // Which release line the daemon updates on: the desktop app (cyborg-v*, updated by
  // the app itself) or the headless CLI (cyborg-cli-v*). Absent on older daemons.
  distribution?: "desktop" | "headless";
  // Outcome of the last update attempt on that machine (last-update.json). Absent
  // until one ran, and on older daemons.
  lastUpdate?: DaemonLastUpdate;
  // The last few outcomes, newest first — find your own request id here.
  recentUpdates?: DaemonLastUpdate[];
  // Desktop only: the app version now running on that machine. Newer than appVersion =
  // the daemon outlived an app update and needs a restart.
  hostAppVersion?: string;
  // Desktop only: where the app runs from. Absent on apps older than this field.
  appLocation?: DaemonAppLocation;
  // Desktop only: the app's updater phase. Absent on apps that cannot report it.
  appUpdateProgress?: DaemonAppUpdateProgress;
  // The RPC ids this daemon actually handles, stamped beside `version`. The one
  // honest feature gate on the daemon→client axis: absent means an older daemon,
  // so a caller must fail CLOSED rather than assume support.
  capabilities?: string[];
  // Took queued wakes at its last hello (wake_commands_v1). Absent: never said (an offline machine).
  wakeCommands?: boolean;
  cyboRuntime?: CyboRuntimeProfile;
}

// Worker designation (workspace-owned machine, $99/machine/mo add-on). Server
// contract field on the daemon ROW (relay-owned spec), NOT DaemonMeta — meta is
// the daemon-published heartbeat. `managerId` is a CLOUD user id: compare it
// only via isMe (session-scope.ts), never `===`.
export interface DaemonWorkerConfig {
  defaultScopes: Array<"chat" | "spawn">;
  managerId: string;
}

export interface Daemon {
  id: string;
  label: string;
  ownerId: string;
  status: string;
  lastSeenAt: number | null;
  meta?: DaemonMeta | null;
  // Optional/back-compat: absent until the relay ships the Worker tier.
  worker?: DaemonWorkerConfig | null;
  // false: a personal machine whose owner left the workspace (absent on an older relay).
  ownerIsMember?: boolean;
  // Desktop only: set when its app cannot update itself (absent on an older relay).
  updateBlocked?: DesktopUpdateBlocked;
  // Old Mac apps: behind the feed, the Repair install offer, the last nudge (newer relays).
  desktopUpdate?: DesktopUpdateStatus;
  // Someone's personal computer this viewer may not see, sent only because an agent they see lives
  // on it: id, liveness, ownerId "" and "an untitled computer"; never listed or offered (DaemonState.masked).
  masked?: true;
}

// The daemon row as the WIRE serves it: the landed relay (#3422) ships the
// Worker designation FLAT (worker boolean + memberDefaultRole/workerManagerId);
// a canonical Daemon (nested worker object) also satisfies this shape, so
// normalizeDaemonRow is idempotent and every load path can route through it.
export interface DaemonWireRow extends Omit<Daemon, "worker"> {
  worker?: boolean | DaemonWorkerConfig | null;
  memberDefaultRole?: string | null;
  workerManagerId?: string | null;
}

export function normalizeDaemonRow(row: DaemonWireRow): Daemon {
  const { worker, memberDefaultRole, workerManagerId, ...rest } = row;
  // A wire that omitted the field stays omitted — materializing `worker: null`
  // here broke exact-equality snapshots of untouched rows (liveness poll test).
  if (worker === undefined) return rest;
  if (worker === null || worker === false) return { ...rest, worker: null };
  if (typeof worker === "object") return { ...rest, worker };
  // Mirrors ROLE_PRESETS viewer/operator (daemon-scopes.ts, not imported — cycle).
  let defaultScopes: Array<"chat" | "spawn"> = [];
  if (memberDefaultRole === "operator") defaultScopes = ["chat", "spawn"];
  else if (memberDefaultRole === "viewer") defaultScopes = ["chat"];
  // managerId "" when the wire omits it: isMe("") never matches, so the
  // manager arm simply stays cold until the relay sends the real cloud id.
  return { ...rest, worker: { defaultScopes, managerId: workerManagerId ?? "" } };
}

export interface DaemonAccessEntry {
  daemonId: string;
  userId: string;
  grantedBy: string;
  grantedAt: number;
  // Capability scopes for this grant (#705): chat | spawn | terminal | admin.
  // Optional for back-compat with an older relay that omits the field — the UI
  // normalizes a missing/empty value to ['admin'] (legacy total access).
  scopes?: string[];
}

// Agents → Skills: a workspace-owned SKILL.md assigned per cybo (relay skills RPCs). An
// assignment has no delivery state: the agent reads the skill from the relay when it needs it.
export interface SkillAssignment {
  id: string;
  skillId: string;
  cyboId: string;
  assignedBy?: string;
  assignedAt: number;
  updatedAt: number;
}

// A script or resource bundled with a skill (skill_files on the relay). A listing carries
// no content: the pane shows paths and sizes, never the bytes.
export interface SkillFileInfo {
  path: string;
  size: number;
  sha256: string;
  encoding: "utf8" | "base64";
  mode: "0644" | "0755";
  updatedAt: number;
}

// A bundled file as cyborg:upsert_skill saves it.
export interface SkillFileUpload {
  path: string;
  content: string;
  encoding: "utf8" | "base64";
  mode: "0644" | "0755";
}

// A bundled file as cyborg:import_skill returns it: the file plus its decoded size.
export interface ImportedSkillFile extends SkillFileUpload {
  size: number;
}

// What cyborg:import_skill reads from GitHub; nothing is stored until the create form saves.
export interface ImportedSkill {
  name: string;
  slug: string;
  description?: string;
  content: string;
  sourceUrl: string;
  // Absent from a relay that predates bundled files.
  files?: ImportedSkillFile[];
  skippedFiles?: number;
}

// A marketplace result's identifiers, which import the skill's whole folder.
export interface SkillImportIdentifiers {
  repo: string;
  ref: string;
  path?: string;
}

// The line icons a skill can wear (the relay's SKILL_ICONS); absent is "default".
export type SkillIcon =
  | "default"
  | "code"
  | "chart"
  | "document"
  | "checklist"
  | "megaphone"
  | "mail"
  | "calendar"
  | "image"
  | "finance"
  | "legal"
  | "terminal";

export type MarketplaceSort = "relevance" | "new" | "installs";

export interface Skill {
  id: string;
  workspaceId: string;
  slug: string;
  name: string;
  description?: string;
  labels?: string[];
  icon?: SkillIcon;
  content: string;
  sourceUrl?: string;
  scope: "workspace" | "project";
  projectId?: string;
  addedBy?: string;
  createdAt: number;
  updatedAt: number;
  assignments: SkillAssignment[];
  // Absent from a relay that predates bundled files.
  files?: SkillFileInfo[];
}

// Public skill marketplaces the relay searches (cyborg:search_skill_marketplaces).
export type MarketplaceSource = "anthropic" | "skills-sh" | "claude-plugins" | "skillsmp";

export interface MarketplaceSkill {
  id: string;
  source: MarketplaceSource;
  alsoIn: MarketplaceSource[];
  name: string;
  description?: string;
  repo: string;
  path?: string;
  // Absent when the marketplace does not expose a fetchable SKILL.md.
  importUrl?: string;
  pageUrl: string;
  installs?: number;
  stars?: number;
  updatedAt?: number;
  // Set when the search filtered by a SkillsMP category.
  category?: string;
}

export interface MarketplaceSourceStatus {
  source: MarketplaceSource;
  ok: boolean;
  count: number;
  error?: string;
}

export interface Cybo {
  id: string;
  slug: string;
  name: string;
  // knowledge/157: "external" replies come from a bot outside Cyborg (provider is "external").
  kind?: "agent" | "external";
  description?: string | null;
  avatar?: string | null;
  role?: string | null;
  provider: string;
  model?: string | null;
  // First ~280 chars of the cybo's soul.md — a personality preview for the card.
  soulExcerpt?: string;
  llmAuthMode?: string;
  behaviorMode?: string;
  // Per-cybo autonomy dial (S2). One of "L0".."L5"; null/undefined → falls back
  // from behaviorMode. Set on the agent profile → updateCybo({autonomyLevel}).
  autonomyLevel?: string | null;
  monthlySpendCap?: number | null;
  platformPermissions?: string[];
  // Composio (+ future) tool capability grants: { composio: [{toolkit, binding, ...}] }.
  // Round-tripped by the cybo editor. Object shape from server CyboToolGrants.
  toolGrants?:
    | { composio?: Array<{ toolkit: string; binding?: string }> }
    | Record<string, unknown>
    | null;
  // Per-agent custom MCP servers ({"name": {command,args,env} | {type:"http",url}}),
  // injected only into this cybo's sessions at spawn. Round-tripped by the editor's
  // "Custom MCP servers" card; absent on older payloads.
  mcpServers?: Record<string, unknown> | null;
  // Danger toggle: run every tool without approval prompts (trusted sandboxes
  // only). Server support lands in a parallel lane; absent on older payloads.
  bypassPermissions?: boolean;
  // Personality. Omitted from the list (fetch_cybos); only the single-cybo
  // fetch (fetch_cybo) populates it for the editor / detail view.
  soul?: string | null;
  // The cybo's explicit "home" daemon — the machine it lives on / runs on,
  // chosen at creation and carried authoritatively across daemons. null/absent
  // for cybos created before this existed → spawn falls back to the
  // sponsor/selected daemon. Distinct from `daemonId` below (which is only the
  // provenance of a disk/local cybo).
  homeDaemonId?: string | null;
  // True for a disk cybo surfaced in the list but not yet in the workspace DB.
  isLocal?: boolean;
  // Home daemon of a LOCAL (disk) cybo — the only machine that can spawn it.
  // null/absent for workspace (DB) cybos, which any daemon can run via the
  // relay's resolvedCybo enrichment.
  daemonId?: string | null;
  isDefault: boolean;
  // userId of the human who created/owns this cybo (cybos.created_by). Present at
  // runtime on every workspace cybo; used to list "agents owned by" a member.
  createdBy?: string | null;
  createdAt: number;
  // Provider readiness (#636), computed server-side from the workspace's live
  // daemon set: "ready" (a reachable daemon can run it), "needs-daemon" (created
  // fine but nothing reachable can run it yet — the UI shows a banner) or
  // "created" (indeterminate, e.g. a native harness whose host login the relay
  // can't observe). Absent on older payloads → treat as unknown, don't warn.
  readiness?: CyboReadiness;
  // epoch ms — last time this cybo was active, = max(agent_sessions.updated_at)
  // for this cybo, computed server-side (PG/relay path). Optional/additive;
  // absent on older relays and on the SQLite-only daemon path (no session
  // table to aggregate), so treat absence as "unknown" → render "—".
  lastActiveAt?: number;
}

export type CyboReadiness = "ready" | "needs-daemon" | "created";

export interface AgentUsage {
  inputTokens?: number;
  cachedInputTokens?: number;
  outputTokens?: number;
  totalCostUsd?: number;
  contextWindowMaxTokens?: number;
  contextWindowUsedTokens?: number;
}

export type ToolCallDetail =
  | { type: "shell"; command: string; cwd?: string; output?: string; exitCode?: number | null }
  | { type: "read"; filePath: string; content?: string; offset?: number; limit?: number }
  | {
      type: "edit";
      filePath: string;
      oldString?: string;
      newString?: string;
      unifiedDiff?: string;
    }
  | { type: "write"; filePath: string; content?: string }
  | {
      type: "search";
      query: string;
      toolName?: string;
      content?: string;
      filePaths?: string[];
      numFiles?: number;
      numMatches?: number;
    }
  | { type: "fetch"; url: string; result?: string; code?: number; bytes?: number }
  | {
      type: "sub_agent";
      subAgentType?: string;
      description?: string;
      // The spawned child session's id, when the provider runs the sub-agent as a
      // real, separately-addressable session (OpenCode). Absent for Claude's Task
      // tool (an in-process sidechain — no child session to open). When present AND
      // it maps to a listable agent, the detail renders a click-through to it.
      childSessionId?: string;
      log: string;
      // Per-action breakdown. Dead on the wire (every producer emits []), kept only
      // for shape parity; the real per-action data is folded into `log` as
      // `[ToolName] summary` lines and recovered by parseSubAgentLog.
      actions?: { index: number; toolName: string; summary?: string }[];
    }
  | { type: "plain_text"; label?: string; text?: string }
  | { type: "plan"; text: string }
  | { type: "worktree_setup"; worktreePath: string; branchName: string; log: string }
  | { type: "unknown"; input: unknown; output: unknown };

export interface ToolCallItem {
  type: "tool_call";
  callId: string;
  name: string;
  status: "running" | "completed" | "failed" | "canceled";
  detail: ToolCallDetail;
  error: unknown | null;
  metadata?: Record<string, unknown>;
}

export interface CompactionItem {
  type: "compaction";
  status: "loading" | "completed";
  trigger?: "auto" | "manual";
  preTokens?: number;
}

export type AgentTimelineItem = (
// messageId mirrors the server timeline item — the Paseo user-turn id used as
// the 'rewind to here' anchor (#649). Optional: not every provider/item sets it.
// pageRef/card (CYBORG-309): a staged /pages reference the daemon PERSISTED on
// the user turn (relay-resolved at send). Carried on the durable timeline item so
// a COLD RELOAD re-renders the PageCard instead of the fenced body — the reload-
// survival the optimistic-only echo (state.svelte.ts `card`) previously lacked.
// promptAuthor (misattribution fix): WHO wrote this turn's INPUT, stamped by the
// daemon ONLY when it was not the surface's human — an agent→agent wake routes the
// OTHER agent's text as the turn input (message-router.ts's user_message append).
// Absent on every human turn and on all pre-fix history, which is why the render
// keeps its human fallback.
| {
      type: "user_message";
      text: string;
      messageId?: string;
      clientMessageId?: string;
      pageRef?: string;
      card?: PageCard;
      promptAuthor?: PromptAuthor;
      // Origin stamp only the daemon's peer-transcript-follower writes (peer-message.ts PeerRelayMark).
      peerRelay?: PeerRelayMark;
      // Thread an agent-authored wake came from; joins an ask reply to its card.
      wakeThreadId?: string;
      // Set only on the wake that is the settled answer to this agent's ask: who answered.
      askReplyFrom?: { cyboId: string; name: string; refused?: boolean };
      // Persisted by the daemon, re-signed by the relay; absent on old history.
      attachments?: Attachment[];
      // The `messages` row this prompt IS (the DM row the relay wrote for it) — what a
      // reaction to the prompt addresses. Not `messageId` (the Paseo identity Rewind uses).
      sourceMessageId?: string;
      // The `messages` row whose forwarded card this turn shows (an id; the relay holds the card).
      forwardedMessageId?: string;
    }
  | { type: "assistant_message"; text: string; messageId?: string }
  | { type: "reasoning"; text: string }
  | ToolCallItem
  | { type: "todo"; items: { text: string; completed: boolean }[] }
  | { type: "error"; message: string }
  // cyborg(upstream-sync 2026-10): a daemon notice (Pi ctx.ui.notify mid-turn, the OpenCode
  // runtime notice). Rendered as a muted system line so a notification never vanishes.
  | { type: "notification"; level: "info" | "warning" | "error"; message: string }
  | CompactionItem
) & {
  // Persisted server timestamp (ISO string) folded onto each served item at the
  // timeline serve points (CYBORG-588: dispatcher.ts + relay-standalone.ts). The
  // client parses it into a per-entry ms `ts` (state.svelte.ts) that drives the
  // agent pane's day separators + hover times. Absent on LIVE-stream items and on
  // pre-CYBORG-588 history — the renderer then falls back to Date.now()/no divider.
  ts?: string;
  // A server-coalesced tool call's FINAL row time (ISO), next to `ts` = its first row. Absent from an older server.
  endedTs?: string;
};

export interface AgentPermissionAction {
  id: string;
  label: string;
  behavior: "allow" | "deny";
  variant?: "primary" | "secondary" | "danger";
}

export interface AgentPermissionRequest {
  id: string;
  provider: string;
  name: string;
  kind: "tool" | "plan" | "question" | "mode" | "other";
  title?: string;
  description?: string;
  input?: Record<string, unknown>;
  detail?: ToolCallDetail;
  actions?: AgentPermissionAction[];
  metadata?: Record<string, unknown>;
}

export type AgentPermissionResponse =
  | {
      behavior: "allow";
      selectedActionId?: string;
      updatedInput?: Record<string, unknown>;
    }
  | {
      behavior: "deny";
      selectedActionId?: string;
      message?: string;
      interrupt?: boolean;
    };

export type AgentEvent =
  | { type: "image_rewrite"; from: string; to: string; messageId?: string }
  | { type: "thread_started"; sessionId: string; provider: string }
  | { type: "turn_started"; provider: string; turnId?: string }
  | { type: "turn_completed"; provider: string; usage?: AgentUsage; turnId?: string }
  | {
      type: "turn_failed";
      provider: string;
      error: string;
      code?: string;
      turnId?: string;
      // The provider's stderr, when its error alone names no class (Claude's exit shape).
      diagnostic?: string;
      // Classified by the daemon (cyborg/message-router → provider-error-classify)
      // when a turn-time provider failure is a known gate (usage_gated / auth /
      // expired / rate_limited). Optional + additive: older daemons omit them and
      // the UI falls back to the raw `error`. Drives the same `providerRemedy`
      // remedy the spawn path shows.
      reasonKind?: ProviderReasonKind | null;
      unavailableReason?: string | null;
      // CYBORG-1667: the seq of the stored failure row this event is (newer daemons only).
      failureSeq?: number;
    }
  | { type: "turn_canceled"; provider: string; reason: string; turnId?: string }
  | { type: "timeline"; item: AgentTimelineItem; provider: string; turnId?: string }
  | {
      type: "permission_requested";
      provider: string;
      request: AgentPermissionRequest;
      turnId?: string;
    }
  | {
      type: "permission_resolved";
      provider: string;
      requestId: string;
      resolution: AgentPermissionResponse;
      turnId?: string;
    }
  | { type: "usage_updated"; provider: string; usage: AgentUsage; turnId?: string }
  | { type: "attention_required"; provider: string; reason: string }
  | { type: "model_changed"; provider: string; runtimeInfo: AgentRuntimeInfo }
  | {
      type: "mode_changed";
      provider: string;
      currentModeId: string | null;
      availableModes: AgentMode[];
    }
  | { type: "thinking_option_changed"; provider: string; thinkingOptionId: string | null };

export interface AgentStreamPayload {
  agentId: string;
  workspaceId?: string;
  event: AgentEvent;
  // The relay's workspace broadcast counter (#499's gap detector), on every frame alike.
  seq?: number;
  // A committed timeline row's own seq; absent on a live delta and from an older relay.
  timelineSeq?: number;
  // The durable life that row belongs to; a different one means a rewind made elsewhere.
  timelineLife?: string;
  // The work session the daemon attributed this row to (committed timeline rows only today;
  // the daemon sends null for a row of no session).
  workSessionId?: string | null;
  // The agent's ordered turn status at this frame (knowledge/126 step 2b).
  turnRecord?: AgentTurnStatus;
  // Legacy alias of turnRecord (#3754); read `turnRecord ?? turnStatus` via wireTurnRecord.
  turnStatus?: AgentTurnStatus;
  // On a turn's terminal frame: the last row it committed and the life it ended in
  // (knowledge/124 T1). Absent from an older daemon.
  turnEnd?: TurnEnd;
}

export interface TurnEnd {
  lastSeq: number;
  life: string;
}

// The client keeps, per agent, the one with the highest statusRev (turn-status.svelte.ts).
export type { AgentTurnStatus } from "./turn-status-schema.js";

export interface AgentStatus {
  agentId: string;
  workspaceId: string;
  status: "idle" | "running" | "error";
  error?: string;
}

// Why a provider that's CONNECTED can still be unusable. Mirrors the server
// classifier (cyborg/provider-error-classify.ts) — keep the unions in sync.
export type ProviderReasonKind =
  | "usage_gated" // authed, but the plan/subscription refuses requests (e.g. Claude "add usage")
  | "auth_invalid" // credentials present but rejected (401 / invalid)
  | "not_configured" // no credentials for this provider
  | "not_installed" // the provider's CLI isn't on that machine at all (CYBORG-687)
  | "expired" // saved credentials expired — reconnect
  | "rate_limited" // temporarily throttled
  | "model_unavailable" // the pinned model belongs to a DIFFERENT provider — not an auth problem
  | "workspace_missing" // the folder the agent runs in is gone (prod 2026-08-11)
  | "internal_error" // the probe crashed in OUR code — not a provider or machine problem
  | "timeout" // UI-only: derived from ProviderInfo.timedOut, never sent as a reasonKind
  | "machine_restarting" // UI-only: a turn_failed because the agent's computer was unreachable (agent-unreachable.ts)
  | "unknown"; // couldn't classify — show the raw text

export interface ProviderInfo {
  id: string;
  label: string;
  description: string;
  available: boolean;
  // `thinkingOptions` is the model's OWN effort list as the daemon reports it
  // (CYBORG-1051). Optional: an older daemon omits it and the UI falls back.
  models: {
    id: string;
    label?: string;
    isDefault?: boolean;
    thinkingOptions?: { id: string; label: string; description?: string; isDefault?: boolean }[];
    defaultThinkingOptionId?: string;
  }[];
  modes: { id: string; label: string; description: string }[];
  defaultModeId: string | null;
  // Populated by the daemon when `available` is false (or when the runtime
  // probe errored): the EXACT, user-facing reason + its classified kind, so the
  // UI can show the right remedy instead of a generic "not connected".
  unavailableReason?: string | null;
  reasonKind?: ProviderReasonKind | null;
  // The daemon's probe missed its deadline (an additive wire flag; reasonKind stays null).
  timedOut?: boolean;
  /** This row's daemon can install it (#3036). Absent = an older daemon: legacy
   *  keys only (provider-setup.ts daemonCanInstall). */
  installable?: boolean;
  // ─── ACP CATALOG ROWS ──────────────────────────────────────────────────────
  //
  // `cyborg:list_providers` answers with the UNION of the daemon's provider
  // registry and the shared ACP catalog, so the picker can show every agent we
  // support — roughly three dozen — instead of only the six builtins plus
  // whatever the host happened to have configured. These fields are present ONLY
  // on catalog rows: agents this daemon knows about but has not been told to run.
  //
  // A catalog row is NOT selectable. It reports `available: false` and carries no
  // detection state, so anything that predates these fields treats it as an
  // unavailable provider — which is exactly right. It becomes selectable by being
  // ADDED (ws-client.addAcpProvider), after which the daemon reports it as an
  // ordinary registry row and these fields are gone.
  catalog?: true;
  /** Where a human goes to install / read about the agent. */
  installLink?: string;
  /** The argv the daemon would run, joined for DISPLAY. Never parsed. */
  command?: string;
  /**
   * What the daemon knows about the agent's binary. `self_fetching` = an
   * `npx`/`uvx`-style runner, nothing to install; `unchecked` = there IS a binary
   * to look for but the probe did not answer in time. Absent from an older daemon:
   * read `binaryOnPath` then (acpBinaryState in provider-detail-model.ts).
   */
  binaryState?: "on_path" | "missing" | "self_fetching" | "unchecked";
  /**
   * The older form of `binaryState`: true = on PATH, false = missing, `null` =
   * self-fetching OR unchecked. A daemon that predates `binaryState` only ever
   * sent `null` for a self-fetching runner.
   */
  binaryOnPath?: boolean | null;
  // The command that signs this provider in on that machine, for DISPLAY, sent by a
  // daemon that knows it (Gemini's npx command and pin). Absent from older daemons.
  signInCommand?: string;
}

export interface AgentRuntimeInfo {
  provider: string;
  sessionId: string | null;
  model?: string | null;
  thinkingOptionId?: string | null;
  modeId?: string | null;
  extra?: Record<string, unknown>;
}

export interface AgentMode {
  id: string;
  label: string;
  description?: string;
}

export interface AgentSlashCommand {
  name: string;
  description: string;
  argumentHint: string;
  // Marks a CLIENT-side Cyborg builtin (e.g. /status, /resume) injected into the
  // Claude agent-session palette. Optional + additive: provider-fetched commands
  // never set it, so the composer can tell a builtin (intercept, never send to
  // the agent) from a real provider slash command. Back-compat with the wire
  // shape (the relay only sends name/description/argumentHint).
  builtin?: "status" | "resume" | "login" | "logout";
}

/**
 * The owning daemon's admission record for one prompt (P03). Read back by
 * `fetchAgentPromptReceipt`; `submitted` means the provider was handed the prompt
 * and this daemon will never learn more, so it is not a failure.
 */
export interface AgentPromptReceipt {
  clientMessageId: string;
  attemptId: string;
  agentId: string;
  admission: "reserved" | "submitted" | "accepted" | "rejected";
  execution: "unknown" | "succeeded" | "failed";
  abandonedAt: number | null;
  updatedAt: number;
}
